import React, { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import CircleIcon from '@mui/icons-material/Circle';
import { Box, CircularProgress, Link, Typography } from '@mui/material';

import { HOTEL_TYPES_BY_INTERNAL_NAME } from '~/consts/offerTypes';

import { PropertyResponse } from '~/services/AccommodationService';
import BedbankService from '~/services/BedbankService';
import OffersService from '~/services/OffersService';
import { customerPortalHost } from '~/services/common';

type PreviewIds = PropertyResponse['connectedOffers'][number]['ids'] & Record<string, string | undefined>;

const CUSTOMER_PORTALS: Record<string, string> = {
  'Luxury Escapes': 'lux',
  Scoopon: 'scoopon',
  Cudo: 'cudo',
  TreatMe: 'treatme',
  Deals: 'deals',
  LEBT: 'lebusinesstraveller',
  'Agent Hub': 'leagenthub',
  'CVFR Holidays': 'leagenthub_cvfr',
};

const buildOfferPageUrl = (tenant: string, path: string) => {
  const tenantObject = { value: tenant };
  const ending = !path.includes('?') ? '?preview=true' : '&preview=true';
  return customerPortalHost(tenantObject) + path + ending;
};

const renderCustomerPortalLinks = (ids: PreviewIds, offerName: string) => {
  const path = `/offer/${ids.slug ?? offerName}/${ids.idSalesforceExternal}`;
  return Object.entries(CUSTOMER_PORTALS).map(([portalName, tenantValue]) => (
    <Box key={portalName} component="span" sx={{ ml: 1 }}>
      <Link href={buildOfferPageUrl(tenantValue, path)} target="_blank" rel="noreferrer">
        {portalName}
      </Link>
    </Box>
  ));
};

const renderBedbankPreviewLinks = (ids: PreviewIds) => (
  <Box component="span" display="flex" gap={1} sx={{ ml: 1 }}>
    <Link
      href={`${window.configs['LUX_CUSTOMER_PORTAL']}/partner/${ids.slug}/${ids.id}?preview=true&previewHash=${ids.previewHash}`}
      target="_blank"
      rel="noreferrer"
    >
      LE
    </Link>
    {ids.externalSlug && (
      <Link href={`${window.configs['EXPEDIA_PORTAL']}/${ids.externalSlug}`} target="_blank" rel="noreferrer">
        EPS
      </Link>
    )}
  </Box>
);

const useOfferData = (offerId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ['offer-preview-data', offerId],
    queryFn: async () => {
      if (!offerId) {
        return null;
      }
      const response = await OffersService.getOffer(offerId);
      return response?.result ?? null;
    },
    enabled: enabled && !!offerId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

const useBedbankPropertyData = (propertyId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ['bedbank-property-preview-data', propertyId],
    queryFn: async () => {
      if (!propertyId) {
        return null;
      }
      const response = await BedbankService.getPropertyEdit(propertyId);
      return response?.result ?? null;
    },
    enabled: enabled && !!propertyId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

export default function AccommodationPreviewLink({
  type,
  name,
  ids = {},
  status = 'inactive',
}: {
  type: string;
  name: string;
  ids?: PreviewIds;
  status?: string;
}) {
  const statusColor = status === 'active' ? 'success' : 'error';

  const isFlashOrLpc = type === HOTEL_TYPES_BY_INTERNAL_NAME.flash || type === HOTEL_TYPES_BY_INTERNAL_NAME.lpc;
  const isLpp =
    type === HOTEL_TYPES_BY_INTERNAL_NAME.lpp ||
    type.toLowerCase() === HOTEL_TYPES_BY_INTERNAL_NAME.bedbank.toLowerCase();

  // Determine if we need to fetch offer data (flash/lpc)
  const needsOfferData = isFlashOrLpc && (!ids.slug || !ids.idSalesforceExternal);
  const offerIdToFetch = needsOfferData ? ids.idSalesforceExternal || ids.id : undefined;

  // Determine if we need to fetch bedbank property data (lpp)
  const needsBedbankData = isLpp && (!ids.slug || !ids.previewHash);
  const bedbankIdToFetch = needsBedbankData ? ids.id : undefined;

  const {
    data: offerData,
    isLoading: isOfferLoading,
    error: offerError,
  } = useOfferData(offerIdToFetch, needsOfferData);

  const {
    data: bedbankData,
    isLoading: isBedbankLoading,
    error: bedbankError,
  } = useBedbankPropertyData(bedbankIdToFetch, needsBedbankData);

  const mergedIds = useMemo((): PreviewIds => {
    const result = { ...ids };

    // Merge offer data for flash/lpc
    if (offerData) {
      if (!result.slug && offerData.slug) {
        result.slug = offerData.slug;
      }
      if (!result.id_salesforce_external && offerData.id_salesforce_external) {
        result.id_salesforce_external = offerData.id_salesforce_external;
      }
    }

    // Merge bedbank data for lpp
    if (bedbankData) {
      if (!result.slug && bedbankData.slug) {
        result.slug = bedbankData.slug;
      }
      if (!result.id && bedbankData.id) {
        result.id = bedbankData.id;
      }
      if (!result.previewHash && bedbankData.previewHash) {
        result.previewHash = bedbankData.previewHash;
      }
      if (!result.externalSlug && bedbankData.externalSlug) {
        result.externalSlug = bedbankData.externalSlug;
      }
    }

    return result;
  }, [ids, offerData, bedbankData]);

  const isLoading = isOfferLoading || isBedbankLoading;
  const error = offerError || bedbankError;

  const links = useMemo(() => {
    if (isLoading) {
      return <CircularProgress size={16} />;
    }

    if (error) {
      return (
        <Typography variant="body2" color="error">
          Failed to load preview links
        </Typography>
      );
    }

    if (isFlashOrLpc) {
      if (!mergedIds.idSalesforceExternal) {
        return (
          <Typography variant="body2" color="text.secondary">
            Missing offer ID
          </Typography>
        );
      }
      return <>{renderCustomerPortalLinks(mergedIds, name)}</>;
    }

    if (isLpp) {
      if (!mergedIds.id || !mergedIds.slug) {
        return (
          <Typography variant="body2" color="text.secondary">
            Missing property data
          </Typography>
        );
      }
      return renderBedbankPreviewLinks(mergedIds);
    }

    return <Typography variant="body1">Links for {type} not currently supported</Typography>;
  }, [type, mergedIds, name, isLoading, error, isFlashOrLpc, isLpp]);

  const shortType = Object.keys(HOTEL_TYPES_BY_INTERNAL_NAME).find((key) => HOTEL_TYPES_BY_INTERNAL_NAME[key] === type);

  return (
    <Box display="grid" gridTemplateColumns="3fr 2fr" gap={2}>
      <Box display="flex" alignItems="top" flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <CircleIcon color={statusColor} />
          <Typography variant="subtitle2" style={{ textTransform: 'uppercase' }}>
            {shortType}
          </Typography>
        </Box>
        <Typography variant="body1">{name}</Typography>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
        {links}
      </Box>
    </Box>
  );
}
